import { fetchApi } from '@/api/fetchApi'
import { ApiFileItem } from '@/features/files/api/types.ts'
import { TranscriptionLanguage } from '@/features/settings/settingsStore'
import { queryClient } from '@/api/queryClient'
import { uploadLimitQueryKey } from './uploadLimit'

/**
 * Upload a file, using XHR so we can report on progress through a handler.
 *
 * @param url The URL to PUT the file to.
 * @param acl Acl to use when uploading the file.
 * @param file The file to upload.
 * @param progressHandler A handler that receives progress updates as a single integer `0 <= x <= 100`.
 */
export const uploadFile = (
  url: string,
  acl: string | null,
  file: File,
  progressHandler: (progress: number) => void
) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    if (acl !== null) {
      xhr.setRequestHeader('X-amz-acl', acl)
    }
    xhr.setRequestHeader('Content-Type', file.type)

    xhr.addEventListener('error', reject)
    xhr.addEventListener('abort', reject)

    xhr.addEventListener('readystatechange', () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          // Make sure to always set the progress to 100% when the upload is done.
          // Because 'progress' event listener is not called when the file size is 0.
          progressHandler(100)
          return resolve(true)
        }
        reject(new Error(`Failed to perform the upload on ${url}.`))
      }
    })

    xhr.upload.addEventListener('progress', (progressEvent) => {
      if (progressEvent.lengthComputable) {
        progressHandler(
          Math.floor((progressEvent.loaded / progressEvent.total) * 100)
        )
      }
    })

    xhr.send(file)
  })

export const createPendingAudioFile = async ({
  filename,
  durationSeconds,
  createdAt,
  source,
  language,
}: {
  filename: string
  durationSeconds: number
  createdAt: string
  source: 'web_recording' | 'web_file_upload'
  language: TranscriptionLanguage
}) => {
  const pendingFile = await fetchApi<ApiFileItem>(`/files/`, {
    method: 'POST',
    body: JSON.stringify({
      created_at: createdAt,
      filename,
      type: 'audio_recording',
      duration_seconds: durationSeconds,
      source,
      language,
    }),
    rateLimitScope: 'upload',
  }).finally(() => {
    // Refresh after creation rejections so
    // usage changes in another tab also produce the limit alert.
    void queryClient.invalidateQueries({ queryKey: uploadLimitQueryKey })
  })

  if (pendingFile.upload_state !== 'pending') {
    throw new Error('State should be pending right after creation')
  }

  return pendingFile
}

export const markUploadEnded = async (fileId: string) => {
  const file = await fetchApi<ApiFileItem>(`/files/${fileId}/upload-ended/`, {
    method: 'POST',
    rateLimitScope: 'upload',
  })
  void queryClient.invalidateQueries({ queryKey: uploadLimitQueryKey })
  return file
}

/**
 * Creates a pending file on the server and uploads the audio to storage.
 * Finalization is left to the caller, so it can be retried on its own.
 *
 * @returns The id of the server file, to pass to `markUploadEnded`.
 */
export const transferAudioFile = async ({
  file,
  durationSeconds,
  onProgress,
  createdAt,
  source,
  language,
}: {
  file: File
  durationSeconds: number
  onProgress: (progress: number) => void
  createdAt: string
  source: 'web_recording' | 'web_file_upload'
  language: TranscriptionLanguage
}): Promise<string> => {
  const pendingFile = await createPendingAudioFile({
    filename: file.name,
    durationSeconds,
    createdAt,
    source,
    language,
  })
  await uploadFile(pendingFile.policy, pendingFile.acl, file, onProgress)
  return pendingFile.id
}
